'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, CalendarCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  format,
  isValid,
  parse,
  isAfter,
  isBefore,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  getYear,
  getMonth,
  setYear,
  setMonth,
  subYears,
} from 'date-fns';

export interface DatePickerProps {
  value?: string;
  onChange: (date: string) => void;
  onBlur?: () => void;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  isDob18Plus?: boolean;
}

export function DatePicker({
  value,
  onChange,
  onBlur,
  minDate,
  maxDate,
  placeholder = 'Select date (DD/MM/YYYY)',
  className,
  required,
  disabled,
  isDob18Plus = false,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const yearListRef = useRef<HTMLDivElement>(null);

  // Safe parse value
  const parseSafe = (val?: string): Date | undefined => {
    if (!val) return undefined;
    // Try YYYY-MM-DD
    let parsed = parse(val, 'yyyy-MM-dd', new Date());
    if (isValid(parsed)) return parsed;
    // Try DD/MM/YYYY
    parsed = parse(val, 'dd/MM/yyyy', new Date());
    if (isValid(parsed)) return parsed;
    // Try fallback parse
    parsed = new Date(val);
    return isValid(parsed) ? parsed : undefined;
  };

  const selectedDate = parseSafe(value);

  const defaultRefDate = isDob18Plus ? subYears(new Date(), 18) : new Date();

  const [viewDate, setViewDate] = useState<Date>(() => selectedDate || defaultRefDate);
  const [viewMode, setViewMode] = useState<'calendar' | 'year' | 'month'>('calendar');

  const effectiveMaxDate = isDob18Plus
    ? (maxDate ? (isBefore(maxDate, subYears(new Date(), 18)) ? maxDate : subYears(new Date(), 18)) : subYears(new Date(), 18))
    : maxDate;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setViewMode('calendar');
        if (onBlur) onBlur();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      if (selectedDate && isValid(selectedDate)) {
        setViewDate(selectedDate);
      } else if (!value) {
        setViewDate(defaultRefDate);
      }
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, value]);

  // Scroll active year into view
  useEffect(() => {
    if (viewMode === 'year' && yearListRef.current) {
      const activeEl = yearListRef.current.querySelector('.active-year');
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'center', behavior: 'instant' });
      }
    }
  }, [viewMode]);

  const handleSelect = (date: Date) => {
    onChange(format(date, 'yyyy-MM-dd'));
    setViewDate(date);
    setIsOpen(false);
    setViewMode('calendar');
    if (onBlur) onBlur();
  };

  const handleClear = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    onChange('');
    setIsOpen(false);
    if (onBlur) onBlur();
  };

  const handleSetMax18 = () => {
    const d = subYears(new Date(), 18);
    handleSelect(d);
  };

  const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
  const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));

  const start = startOfMonth(viewDate);
  const end = endOfMonth(viewDate);
  const startDay = start.getDay();
  const paddingDays = Array.from({ length: startDay }).map((_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() - (startDay - i));
    return d;
  });
  const daysInMonth = eachDayOfInterval({ start, end });
  const endDay = end.getDay();
  const trailingDays = Array.from({ length: 6 - endDay }).map((_, i) => {
    const d = new Date(end);
    d.setDate(d.getDate() + i + 1);
    return d;
  });

  const allDays = [...paddingDays, ...daysInMonth, ...trailingDays];

  const currentYear = getYear(new Date());
  const maxSelectableYear = isDob18Plus ? currentYear - 18 : currentYear + 20;
  const years = Array.from({ length: 120 }).map((_, i) => maxSelectableYear - i);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <div className={cn('relative w-full', className)} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setIsOpen(!isOpen);
            setViewMode('calendar');
          }
        }}
        className={cn(
          'w-full flex items-center justify-between px-3.5 py-2.5 text-sm font-medium rounded-xl border bg-white shadow-sm transition-all text-left outline-none',
          isOpen
            ? 'border-[#3b5444] ring-2 ring-[#3b5444]/20 shadow-md'
            : 'border-[#d5cfc2] hover:border-[#3b5444]/60',
          disabled && 'opacity-60 cursor-not-allowed bg-gray-50'
        )}
      >
        <span className="flex items-center gap-2.5 truncate">
          <CalendarIcon className="w-4 h-4 text-[#3b5444] shrink-0" />
          {selectedDate && isValid(selectedDate) ? (
            <span className="text-[#2d3536] font-semibold tracking-wide">
              {format(selectedDate, 'dd/MM/yyyy')} <span className="text-xs text-[#6e7d80] font-normal">({format(selectedDate, 'dd MMMM yyyy')})</span>
            </span>
          ) : (
            <span className="text-gray-400 font-normal">{placeholder}</span>
          )}
        </span>

        <div className="flex items-center gap-1 shrink-0 ml-2">
          {value && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClear();
                }
              }}
              className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
              title="Clear date"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <CalendarCheck className="w-4 h-4 text-[#6e7d80]" />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-2 w-full sm:w-[350px] bg-white border border-[#e2ddd3] shadow-2xl rounded-2xl p-4 animate-in fade-in zoom-in-95 duration-200">
          {/* Header Controls */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#e2ddd3]/60">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1.5 hover:bg-[#f5f2e9] text-[#2d3536] rounded-xl transition-colors"
              title="Previous Month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'month' ? 'calendar' : 'month')}
                className={cn(
                  'text-sm font-bold px-2.5 py-1 rounded-xl transition-colors',
                  viewMode === 'month'
                    ? 'bg-[#3b5444] text-white shadow-sm'
                    : 'text-[#2d3536] hover:bg-[#f5f2e9]'
                )}
              >
                {format(viewDate, 'MMMM')}
              </button>

              <button
                type="button"
                onClick={() => setViewMode(viewMode === 'year' ? 'calendar' : 'year')}
                className={cn(
                  'text-sm font-bold px-2.5 py-1 rounded-xl transition-colors',
                  viewMode === 'year'
                    ? 'bg-[#3b5444] text-white shadow-sm'
                    : 'text-[#2d3536] hover:bg-[#f5f2e9]'
                )}
              >
                {format(viewDate, 'yyyy')}
              </button>
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1.5 hover:bg-[#f5f2e9] text-[#2d3536] rounded-xl transition-colors"
              title="Next Month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                  <div key={d} className="text-xs font-bold text-[#6e7d80] uppercase tracking-wider py-1">
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1.5">
                {allDays.map((day, i) => {
                  const isCurrentMonth = isSameMonth(day, viewDate);
                  const isSelected = selectedDate && isValid(selectedDate) && isSameDay(day, selectedDate);
                  const isToday = isSameDay(day, new Date());
                  const disabledDay =
                    (minDate && isBefore(day, minDate)) ||
                    (effectiveMaxDate && isAfter(day, effectiveMaxDate));

                  return (
                    <button
                      key={i}
                      type="button"
                      disabled={disabledDay}
                      onClick={() => handleSelect(day)}
                      className={cn(
                        'h-9 w-9 sm:h-10 sm:w-10 rounded-xl flex items-center justify-center transition-all text-sm font-semibold mx-auto',
                        isSelected
                          ? 'bg-[#3b5444] text-white font-bold shadow-md ring-2 ring-[#3b5444]/20 scale-105'
                          : isToday
                            ? 'border-2 border-[#3b5444] text-[#3b5444] font-bold bg-[#3b5444]/5'
                            : 'hover:bg-[#f5f2e9] text-[#2d3536]',
                        !isCurrentMonth && !isSelected && 'text-gray-300 opacity-60',
                        disabledDay && 'opacity-25 cursor-not-allowed hover:bg-transparent hover:text-gray-300'
                      )}
                    >
                      {format(day, 'd')}
                    </button>
                  );
                })}
              </div>

              {/* Quick Actions Footer */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#e2ddd3]/60">
                {isDob18Plus ? (
                  <button
                    type="button"
                    onClick={handleSetMax18}
                    className="text-xs font-bold text-[#3b5444] hover:underline"
                  >
                    18 Years Ago ({format(subYears(new Date(), 18), 'yyyy')})
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSelect(new Date())}
                    className="text-xs font-bold text-[#3b5444] hover:underline"
                  >
                    Today
                  </button>
                )}

                {value && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-xs font-bold text-red-500 hover:text-red-700"
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          )}

          {/* Year Picker Grid */}
          {viewMode === 'year' && (
            <div ref={yearListRef} className="h-60 overflow-y-auto pr-1 grid grid-cols-3 gap-2 py-1">
              {years.map((y) => {
                const isActive = getYear(viewDate) === y;
                return (
                  <button
                    key={y}
                    type="button"
                    onClick={() => {
                      setViewDate(setYear(viewDate, y));
                      setViewMode('calendar');
                    }}
                    className={cn(
                      'text-sm py-2.5 rounded-xl font-bold transition-all',
                      isActive
                        ? 'bg-[#3b5444] text-white shadow-md active-year'
                        : 'text-[#2d3536] hover:bg-[#f5f2e9]'
                    )}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}

          {/* Month Picker Grid */}
          {viewMode === 'month' && (
            <div className="grid grid-cols-3 gap-2 py-2">
              {months.map((m, i) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setViewDate(setMonth(viewDate, i));
                    setViewMode('calendar');
                  }}
                  className={cn(
                    'text-sm py-3 rounded-xl font-bold transition-all',
                    getMonth(viewDate) === i
                      ? 'bg-[#3b5444] text-white shadow-md'
                      : 'text-[#2d3536] hover:bg-[#f5f2e9]'
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
